import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import { salesManagement } from "../../../config";
import { AllVendors } from "../../../data/Axios/queries";
import VendorsForm from "./form";
import { useEffect, useState } from "react";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { tokens } from "../../../theme";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";

const base_url = salesManagement.uri;

const actions = {
  entity: "Vendors",
  edit: {
    key: "EDIT",
    button_name: "Edit",
    Show_Button: true,
    permission: "EditPermission",
  },
  add: {
    key: "ADD",
    button_name: "Add",
    Show_Button: true,
    permission: "CreatePermission",
  },
  activateDeactivate: {
    key: "ACTIVATEDEACTIVATE",
    button_name: "Deactivate",
    Show_Button: true,
    permission: "ActivatePermission",
    modelName: "productIds",
    inputObjectName: "bulkActivateDeactivateVendors",
  },
  ViewSummary: {
    key: "VIEWACCOUNTSUMMARY",
    button_name: "Account Summary",
    Show_Button: true,
    permission: "CreatePermission",
  },
  showMore: {
    key: "SHOWMORE",
    button_name: "Show More",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
    title: "Vendor Details",
  },
};

function VendorsTable() {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [branches, setBranches] = useState([]);

  // Statistics
  const [stats, setStats] = useState({
    totalVendors: 0,
    totalBalance: 0,
    activeVendors: 0,
    inactiveVendors: 0,
    vendorsWithBalance: 0,
    averageBalance: 0,
  });

  // MUI theme-aware colors
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isDark = muiTheme.palette.mode === "dark";

  const theme = {
    primary: isDark ? colors.blueAccent[400] : "#1976D2",
    secondary: isDark ? colors.blueAccent[500] : "#0288D1",
    success: "#4CAF50",
    warning: "#FF9800",
    error: "#F44336",
    info: isDark ? colors.blueAccent[300] : "#2196F3",
    lightBg: isDark ? alpha(colors.blueAccent[400], 0.15) : "#E3F2FD",
    cardBg: isDark ? colors.primary[400] : "#FFFFFF",
    pageBg: isDark ? colors.primary[500] : "#F5F7FA",
    iconBadgeGreen: isDark ? alpha("#4CAF50", 0.15) : "#E8F5E9",
    iconBadgeRed: isDark ? alpha("#F44336", 0.15) : "#FFEBEE",
  };

  const ignore = ["isActive", "email", "companyId", "branchId", "isInternal", "createdBy", "updatedBy", "createdDate", "updatedDate"];

  // ✅ Calculate statistics
  const calculateStats = (data) => {
    const totalVendors = data.length;
    const totalBalance = data.reduce((sum, vendor) => sum + (vendor.balance || 0), 0);

    const activeVendors = data.filter(vendor => vendor.isActive === true || vendor.isActive === 1).length;
    const inactiveVendors = data.filter(vendor => vendor.isActive === false || vendor.isActive === 0).length;

    const vendorsWithBalance = data.filter(vendor => (vendor.balance || 0) > 0).length;
    const averageBalance = totalVendors > 0 ? totalBalance / totalVendors : 0;

    setStats({
      totalVendors,
      totalBalance,
      activeVendors,
      inactiveVendors,
      vendorsWithBalance,
      averageBalance,
    });
  };

  // ✅ Extract unique branches
  const extractFilterOptions = (data) => {
    // Assuming branchName isn't directly in AllVendors based on previous view, 
    // but if it is or if we can derive it. 
    // The query showed 'branchId' but not 'branchName'. 
    // If branchName is missing, we might skip branch filter or map it.
    // For now, let's see if we can use it if available, or just skip.
    // Based on query: id, vendorName, contactPerson, ..., branchId
    // We don't have branchName in the query shown earlier. 
    // I will comment out branch extraction if it's not straightforward, 
    // or relying on DynamicTable to handle it if we passed data.
    // But since we are doing manual filtering, we need the data.
    // Let's assume for now we don't have branch names to filter by effectively 
    // unless we fetch branches too. I will omit branch filter for now to avoid breaking.
  };

  // ✅ Fetch vendors
  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await graphqlQuery(AllVendors, base_url);

      if (Array.isArray(response)) {
        setVendors(response);
        applyFilters(
          response,
          searchQuery,
          statusFilter,
          balanceFilter
        );
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setError("Failed to load vendors. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Apply all filters
  const applyFilters = (data, search, status, balance) => {
    let filtered = [...data];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((vendor) => {
        const fields = [
          vendor.vendorName,
          vendor.contactPerson,
          vendor.phoneNumber,
          vendor.address,
        ];

        return fields.some((field) => {
          if (field == null) return false;
          return String(field).toLowerCase().includes(searchLower);
        });
      });
    }

    // Status filter
    if (status !== "all") {
      // Assuming isActive is boolean
      const statusValue = status === "true";
      filtered = filtered.filter((vendor) => vendor.isActive === statusValue);
    }

    // Balance filter
    if (balance !== "all") {
      if (balance === "with_balance") {
        filtered = filtered.filter((vendor) => (vendor.balance || 0) > 0);
      } else if (balance === "zero_balance") {
        filtered = filtered.filter((vendor) => (vendor.balance || 0) <= 0);
      }
    }

    setFilteredVendors(filtered);
    calculateStats(filtered);
  };

  // ✅ Handle filter changes
  useEffect(() => {
    if (vendors.length > 0) {
      applyFilters(vendors, searchQuery, statusFilter, balanceFilter);
    }
  }, [searchQuery, statusFilter, balanceFilter]);

  // ✅ Handle refresh
  const handleRefresh = () => {
    fetchVendors();
  };

  // ✅ Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setBalanceFilter("all");
  };

  // ✅ Check if filters are active
  const hasActiveFilters =
    searchQuery ||
    statusFilter !== "all" ||
    balanceFilter !== "all";

  // ✅ Fetch data on component mount
  useEffect(() => {
    fetchVendors();
  }, []);

  return (
    <Box sx={{ p: 3, bgcolor: theme.pageBg, minHeight: "100vh" }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <StorefrontIcon sx={{ fontSize: 32, color: theme.primary, mr: 1 }} />
          <Typography variant="h4" fontWeight="700" color={theme.primary}>
            Vendor Management
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Manage your supplier relationships and accounts
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Total Vendors Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            elevation={0}
            sx={{
              background: isDark
                ? `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
                : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
              color: "white",
              border: isDark ? `1px solid ${alpha(colors.blueAccent[400], 0.4)}` : "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <PeopleIcon sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total Vendors
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="700">
                {stats.totalVendors}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                {stats.activeVendors} Active • {stats.inactiveVendors} Inactive
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Outstanding Balance Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Outstanding Balance
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color={theme.error}>
                    KSh{" "}
                    {stats.totalBalance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stats.vendorsWithBalance} vendors with balance
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: theme.iconBadgeRed, p: 1.5, borderRadius: 2 }}>
                  <AttachMoneyIcon sx={{ color: theme.error, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Vendor Status Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Vendor Status
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={theme.success}>
                        {stats.activeVendors}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={theme.warning}>
                        {stats.inactiveVendors}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Inactive
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: theme.iconBadgeGreen, p: 1.5, borderRadius: 2 }}>
                  <CheckCircleIcon sx={{ color: theme.success, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Actions Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          bgcolor: theme.cardBg,
        }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon sx={{ color: theme.primary }} />
          <Typography variant="h6" fontWeight="600">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${[
                searchQuery,
                statusFilter !== "all",
                balanceFilter !== "all",
              ].filter(Boolean).length
                } Active`}
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, contact, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Balance Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Balance</InputLabel>
              <Select
                value={balanceFilter}
                label="Balance"
                onChange={(e) => setBalanceFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="with_balance">With Balance</MenuItem>
                <MenuItem value="zero_balance">Zero / Negative</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Actions */}
          <Grid item xs={12} md={1}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    bgcolor: theme.lightBg,
                    "&:hover": { bgcolor: theme.primary, color: "white" },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery("")}
                color="primary"
                variant="outlined"
              />
            )}
            {statusFilter !== "all" && (
              <Chip
                label={`Status: ${statusFilter === "true" ? "Active" : "Inactive"}`}
                size="small"
                onDelete={() => setStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {balanceFilter !== "all" && (
              <Chip
                label={`Balance: ${balanceFilter === "with_balance" ? "With Balance" : "Zero/Negative"}`}
                size="small"
                onDelete={() => setBalanceFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Main Table */}
      <DynamicTable
        title="Vendors List"
        subtitle="Manage your vendors"
        ignoreFields={ignore}
        FormComponent={VendorsForm}
        query={AllVendors}
        filteredData={filteredVendors}
        isfilteredData={true}
        base_url={base_url}
        actions={actions}
        loading={loading}
      />
    </Box>
  );
}

export default VendorsTable;
